import create from 'zustand';
import cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';

import { MutualActionPlan, MutualActionPlanItem } from 'src/common/interfaces/mutual_action_plan.interface';
import { subscribeWithSelector } from 'zustand/middleware';
import { MutualActionPlanAction } from './types';
import { mutualActionPlanInverseActions } from './inverse-action';
import { Nullable } from 'src/types/nullable.type';

interface Methods {
  // mutualActionPlans is a dictionary of mutualActionPlans indexed by MutualActionPlan.uuid
  getMutualActionPlan: (uuid: MutualActionPlan['uuid']) => MutualActionPlan;
  createMutualActionPlan: (actionPlan: MutualActionPlan) => void;
  updateMutualActionPlan: (actionPlan: MutualActionPlan, params: any) => void;
  deleteMutualActionPlan: (actionPlanUuid: string) => void;
  createMutualActionPlanItem: (actionPlanUuid: string, actionItem: Pick<MutualActionPlanItem, 'uuid' | 'position'>) => void;
  updateMutualActionPlanItem: (actionPlanUuid: string, actionItem: MutualActionPlanItem) => void;
  deleteMutualActionPlanItem: (actionPlanUuid: string, actionItem: Pick<MutualActionPlanItem, 'uuid'>) => void;
  resetMutualActionPlans: () => void;
  dispatch: (action: MutualActionPlanAction) => Nullable<MutualActionPlanAction>;
  initMutualActionPlan: (actionPlan: MutualActionPlan) => void;
}

export type MutualActionPlans = Record<MutualActionPlan['uuid'], MutualActionPlan>;
interface Variables {
  mutualActionPlans: MutualActionPlans;
}

const initialState: Variables = {
  mutualActionPlans: {},
};

type StoreState = Variables & Methods;

export const useMutualActionPlansContext = create<StoreState>(subscribeWithSelector((set: any, get: any) => ({
  ...initialState,
  getMutualActionPlan: (uuid: MutualActionPlan['uuid']) => {
    return get().mutualActionPlans[uuid];
  },
  initMutualActionPlan: (actionPlan: MutualActionPlan) => {
    const { mutualActionPlans } = get();
    const newMutualActionPlans = {
      ...mutualActionPlans,
      [actionPlan.uuid]: actionPlan,
    }
    set({ mutualActionPlans: newMutualActionPlans });
  },
  dispatch: (action: MutualActionPlanAction): Nullable<MutualActionPlanAction> => {
    const { type } = action;
    const {
      mutualActionPlans: oldState,
      createMutualActionPlan,
      updateMutualActionPlan,
      deleteMutualActionPlan,
      createMutualActionPlanItem,
      deleteMutualActionPlanItem,
      updateMutualActionPlanItem
    } = get();

    let newState;
    if (type === 'create-mutual-action-plan') {
      newState = createMutualActionPlan({
        uuid: action.mutualActionPlan.uuid,
        title: '',
        items: [],
      }
      )
    } else if (type === 'delete-mutual-action-plan') {
      newState = deleteMutualActionPlan(action.mutualActionPlan.uuid);
    } else if (type === 'update-mutual-action-plan') {
      newState = updateMutualActionPlan(action.mutualActionPlan.uuid, { title: action.mutualActionPlan.title } );
    } else if (type === 'update-mutual-action-plan-item') {
      newState = updateMutualActionPlanItem(
        action.mutualActionPlan.uuid,
        action.item,
      )
      // maybe resize the block?
    } else if (type === 'create-mutual-action-plan-item') {
      newState = createMutualActionPlanItem(
        action.mutualActionPlan.uuid,
        action.item,
      )
      // maybe resize the block?
      // useMutualActionPlansContext.getState().
    } else if (type === 'delete-mutual-action-plan-item') {
      newState = deleteMutualActionPlanItem(
        action.mutualActionPlan.uuid,
        action.item,
      )
    }
    set({ mutualActionPlans: newState });
    return mutualActionPlanInverseActions(action, oldState, newState);
  },
  createMutualActionPlan: (actionPlan: MutualActionPlan): MutualActionPlans => {
    const { mutualActionPlans } = get();
    return {
      ...mutualActionPlans,
      [actionPlan.uuid]: actionPlan,
    }
  },
  createMutualActionPlanItem: (actionPlanUuid: string, actionItem: Pick<MutualActionPlanItem, 'uuid' | 'position'>): MutualActionPlans => {
    const { mutualActionPlans } = get();
    return {
      ...mutualActionPlans,
      [actionPlanUuid]: {
        ...mutualActionPlans[actionPlanUuid],
        items: [
          ...mutualActionPlans[actionPlanUuid].items,
          {
            uuid: actionItem.uuid,
            position: actionItem.position,
            title: '',
            description: '',
            completed: false,
            assigned_to: '',
          },
        ]
      },
    }
  },
  updateMutualActionPlan: (actionPlan: MutualActionPlan, params = {}): MutualActionPlans => {
    const { mutualActionPlans } = get();
    return {
      ...mutualActionPlans,
      [actionPlan.uuid]: {
        ...mutualActionPlans[actionPlan.uuid],
        ...params,
      },
    }
  },
  deleteMutualActionPlan: (actionPlanUuid: string): MutualActionPlans => {
    const { mutualActionPlans } = get();
    const { [actionPlanUuid]: _, ...restMutualActionPlans } = mutualActionPlans;
    return restMutualActionPlans
  },
  updateMutualActionPlanItem: (actionPlanUuid: string, actionItem: MutualActionPlanItem): MutualActionPlans => {
    const { mutualActionPlans } = get();
    let items = mutualActionPlans[actionPlanUuid].items;
    const index = findIndex(items, { uuid: actionItem.uuid });
    items.splice(index, 1, cloneDeep(actionItem));

    return {
      ...mutualActionPlans,
      [actionPlanUuid]: {
        ...mutualActionPlans[actionPlanUuid],
        items: items,
      }
    }
  },
  deleteMutualActionPlanItem: (actionPlanUuid: string, actionItem: Pick<MutualActionPlanItem, 'uuid'>): MutualActionPlans => {
    const { mutualActionPlans } = get();
    return {
      ...mutualActionPlans,
      [actionPlanUuid]: {
        ...mutualActionPlans[actionPlanUuid],
        items: mutualActionPlans[actionPlanUuid].items.filter((item: MutualActionPlanItem) => item.uuid !== actionItem.uuid),
      },
    }
  },

  resetMutualActionPlans: () => {
    set((state: any) => ({
      ...state,
      mutualActionPlans: {},
    }));
  },
})));
